import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { useGameState } from '../../context/GameStateContext';
import { useMetadata } from '../../context/MetadataContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { useTournaments } from '../../hooks/useTournaments';
import Modal from '../Modal/Modal';
import TournamentDetail from '../TournamentDetail/TournamentDetail';
import styles from './TournamentFind.module.css';

const TournamentFind = () => {
  const { getText } = useMetadata();
  const { triggerError } = useGlobalError();
  const { 
    gameData,
    loading, 
    error, 
    findTournamentPuzzle,
  } = useGameState();
  
  // Tournament data (loaded by the hook)
  const {
    joinedTournaments,
    availableTournaments,
    tournamentsLoading,
    selectedTournamentDetails,
    detailsLoading,
    joinError,
    loadTournamentDetails,
    clearJoinError,
  } = useTournaments();
  
  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAvailableTournament, setSelectedAvailableTournament] = useState(null);
  
  // Early return AFTER all hooks are called
  if (gameData?.state) {
    triggerError({
      type: 'critical',
      message: 'TournamentFind component rendered when puzzle already selected. This indicates a routing/status determination error.'
    });
    return null;
  }
  
  // Handle joined tournament selection - find a puzzle for this tournament
  const handleJoinedTournamentClick = (tournament) => {
    findTournamentPuzzle(tournament.tournament_key);
  };
  
  // Handle available tournament selection - fetch details and show modal
  const handleAvailableTournamentClick = async (tournament) => {
    setSelectedAvailableTournament(tournament);
    setShowDetailModal(true);
    await loadTournamentDetails(tournament.tournament_key);
  };
  
  // Handle closing the detail modal
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAvailableTournament(null);
    clearJoinError();
  };
  
  // Format date from timestamp using browser locale
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const locale = navigator.language || 'en-US';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to render a tournament row
  const renderTournamentRow = (tournament, isJoined = false) => {
    const handleClick = isJoined 
      ? () => handleJoinedTournamentClick(tournament)
      : () => handleAvailableTournamentClick(tournament);
    
    return (
      <div 
        key={tournament.tournament_key} 
        className={`${styles.tournamentRow} ${isJoined ? styles.joinedTournament : ''}`}
        onClick={handleClick}
      >
        <div className={styles.tournamentName}>{tournament.name}</div>
        <div className={styles.tournamentPuzzles}>{tournament.puzzle_count}</div>
        <div className={styles.tournamentStart}>{formatDate(tournament.starts_ts)}</div>
        <div className={styles.tournamentEnd}>{formatDate(tournament.expires_ts)}</div>
      </div>
    );
  };

  // Helper function to render tournament list with header
  const renderTournamentList = (tournaments, isJoined = false) => {
    if (tournaments.length === 0) return null;
    
    return (
      <div className={styles.tournamentListContainer}>
        <div className={styles.tournamentListHeader}>
          <div className={styles.tournamentName}>{getText('common_text.name')}</div>
          <div className={styles.tournamentPuzzles}>{getText('common_text.puzzles')}</div>
          <div className={styles.tournamentStart}>{getText('common_text.start')}</div>
          <div className={styles.tournamentEnd}>{getText('common_text.end')}</div>
        </div>
        <div className={styles.tournamentList}>
          {tournaments.map(tournament => renderTournamentRow(tournament, isJoined))}
        </div>
      </div>
    );
  };
  
  // Loading state
  if (tournamentsLoading) {
    return <div className={styles.loading}>Loading tournaments...</div>;
  }
  
  return (
    <div className={`dataContainer ${styles.tournamentContainer}`}>
      <div className="containerHeader">
          <h2>{getText('tournament_mode.find_title')}</h2>
        </div>
      <div className="dataContent">
      <div className="contentGroup">
        <div className="groupHeader">
          <h3>{getText('tournament_mode.how_title')}</h3>
        </div>
        
        <div className="groupDescription">
          {getText('tournament_mode.find_description')}
        </div>
      </div>
      
      <div className="contentGroup">
        <div className="groupHeader">
          <h3>{getText('tournament_mode.joined_title')}</h3>
        </div>
        {joinedTournaments.length > 0 ? (
          renderTournamentList(joinedTournaments, true)
        ) : (
          <p className={styles.noTournamentsmessage}>
            {getText('tournament_mode.no_joined')}
          </p>
        )}
      </div>
      
      <div className="contentGroup">
        <div className="groupHeader">
          <h3>{getText('tournament_mode.available_title')}</h3>
        </div>
        {availableTournaments.length > 0 ? (
          renderTournamentList(availableTournaments, false)
        ) : (
          <p className={styles.noTournamentsmessage}>
            {getText('tournament_mode.no_available')}
          </p>
        )}
      </div>
      </div>
      
      <Modal 
        isOpen={showDetailModal} 
        onClose={handleCloseDetailModal}
        title={getText('tournament_mode.tournament_details')}
        data-testid="modal"
      >
        {selectedAvailableTournament && (
          <>
            {joinError && (
              <div className={styles.errorMessage}>
                {joinError}
              </div>
            )}

            
            {detailsLoading ? (
              <div className={styles.loading}>
                {getText('common_text.loading')}
              </div>
            ) : selectedTournamentDetails ? (
              <TournamentDetail 
                tournamentData={selectedTournamentDetails}
                onBack={handleCloseDetailModal}
                data-testid="tournament-detail"
              />
            ) : (
              <div className={styles.errorMessage}>
                {getText('tournament_detail.error', 'Failed to load tournament details. Please try again.')}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default TournamentFind;